import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, FieldTypes, useResponsive } from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';
import { DEFAULT_DATE_FORMAT, KEYS } from '@/common/constants';
import { useSearchParams } from '@/modules/transaction/hooks/useSearchParams';
import { centeredFieldStyles } from '@/modules/user/components/SignUp/signUp.constants';
import { useAuthStore } from '@/modules/user/stores';
import { isDateInFuture } from '@/utils';
import whiteLabelingConfig, { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

const isGustoTheme = THEMES.gusto === whiteLabelingConfig.title;

enum UniqueIdTypes {
  SSN = 'ssn',
  EEID = 'eeid',
  PEID = 'peid',
}

interface Props {
  isOrphanMode?: boolean;
  isDisabledMode?: boolean;
  isError?: boolean;
  onNext?: () => void;
}

export default ({ isOrphanMode, isDisabledMode, isError, onNext }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { query: source } = useSearchParams('source');
  const { query: partner } = useSearchParams('partner');
  const { user } = useAuthStore();
  const email = useMemo(() => user?.preferredEmail, [user]);

  const uniqueIdLabel = useMemo(() => {
    const preparedSource = source?.toLowerCase();
    if (preparedSource === UniqueIdTypes.SSN) {
      return t('SSN');
    }
    if (preparedSource === UniqueIdTypes.EEID) {
      return t('Employee ID');
    }
    if (preparedSource === UniqueIdTypes.PEID && partner) {
      return t(`${partner} ID`);
    }
    if (preparedSource === UniqueIdTypes.PEID) {
      return t('Partner ID');
    }
    if (isGustoTheme) return t('Registration ID');
    return t('Unique ID (typically SSN)');
  }, [partner, source, t]);

  const uniqueIdPlaceholder = useMemo(() => {
    const preparedSource = source?.toLowerCase();
    if (preparedSource === UniqueIdTypes.SSN) {
      return t('SSN');
    }
    if (preparedSource === UniqueIdTypes.EEID) {
      return t('Employee ID');
    }
    if (preparedSource === UniqueIdTypes.PEID && partner) {
      return t(`${partner} ID`);
    }
    if (preparedSource === UniqueIdTypes.PEID) {
      return t('Partner ID');
    }
    return t('Unique ID');
  }, [partner, source, t]);

  const handlePressNext = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === KEYS.ENTER && onNext) {
        onNext();
      }
    },
    [onNext]
  );
  const fields = useMemo<Field[]>(() => {
    if (!isOrphanMode) {
      return [
        {
          name: 'birthdate',
          type: FieldTypes.DateInput,
          isManualDateInput: true,
          label: 'Date of Birth',
          isLarge: true,
          showRequireIcon: true,
          disabled: isDisabledMode,
          placeholder: DEFAULT_DATE_FORMAT,
          isError: isError,
          enterKeyHint: 'done',
          validator: VALIDATORS.DATE_FORMAT.required('Date of birth is required').test(
            'date',
            'The date is invalid',
            (date) => !isDateInFuture(date)
          ),
          ...(isMobile ? {} : centeredFieldStyles),
        },
        {
          name: 'uniqueId',
          type: FieldTypes.Text,
          showRequireIcon: true,
          isLarge: true,
          label: uniqueIdLabel,
          placeholder: uniqueIdPlaceholder,
          isError: isError,
          validator: VALIDATORS.STRING.required(t(`${uniqueIdLabel} is required`)),
          disabled: isDisabledMode,
          enterKeyHint: 'go',
          onKeyDown: handlePressNext,

          ...(isMobile ? {} : centeredFieldStyles),
        },
      ];
    }
    return [
      {
        name: 'email',
        type: FieldTypes.Email,
        label: 'Personal email',
        isLarge: true,
        placeholder: 'Email',
        showRequireIcon: true,
        defaultValue: email,
        enterKeyHint: 'go',
        onKeyDown: handlePressNext,
        validator: VALIDATORS.STRING.email('Email must be valid').required(t('Email is required')),
        ...(isMobile ? {} : centeredFieldStyles),
      },
    ];
  }, [
    isOrphanMode,
    email,
    t,
    isMobile,
    isDisabledMode,
    isError,
    uniqueIdLabel,
    uniqueIdPlaceholder,
    handlePressNext,
  ]);
  return fields;
};
